import * as React from 'react';
import { Intent, Button, Tooltip, H4, Spinner, Icon, H3, Card } from '@blueprintjs/core';
import { LoadableSVG } from '../LoadableSVG';
import { CoachingAudio } from '../CoachingAudio';
import { executeQueryPostAuth, executeQueryGetAuth } from 'app/utils/api';
import { UserState } from 'app/reducers/state';
import { KryPlayDetails } from '../KryPlayDetails';
import { SingleOverview } from '../SingleOverview';
import TextAreaV2 from '../TextAreaV2';
import CoachFeedback from '../CoachFeedback';

interface Props {
    jouranlGraphic: string;
    coachingAudio: string;
    gameID: string;
    user: UserState;
    slug: string;
    promptData: any;
    prompt: string;
    blockObjectives?: boolean;
    recieveJournal: (entry: string) => void;
    goBackHome: () => void;
    cachedDefaultJournal?: string;
    showPreGraph?: boolean;
    initialStylesV1?: boolean;
};

interface State {
    isJournalVisible: boolean;
    hasAudioFinishedOnce: boolean;
    writtenJouranl: string;
    loadingObjectiveData: boolean;
    objectiveData: any;
    hasViewedObjectives: boolean;
    viewGraphic: boolean;
    hasViewedAllObjectives: boolean;
    promptData: any;
    promptInput: any;
    showPreGraph: boolean;
    stagePromptForFinalPass: string;
    finalPassResults: any;
    aiCoachResults: any;
    aiCoachLoading: boolean;
    hasEvaluated: boolean;
}

export class JournalWriter extends React.Component<Props, State> {

    state: State = {
        isJournalVisible: false,
        hasAudioFinishedOnce: false,
        writtenJouranl: '',
        loadingObjectiveData: true,
        hasViewedObjectives: false,
        hasViewedAllObjectives: false,
        viewGraphic: true,
        objectiveData: {},
        promptData: {},
        promptInput: {},
        showPreGraph: false,
        stagePromptForFinalPass: "",
        finalPassResults: undefined,
        aiCoachResults: undefined,
        aiCoachLoading: false,
        hasEvaluated: false
    }

    componentDidMount() {
        if (this.props.slug.includes("_initial")) {
            this.setState({
                hasEvaluated: true
            })
        }

        const promptData = this.props.promptData;

        let newPromptInput = {};

        if (this.props.user.classicMode === 2) {
            if (promptData && promptData.classic) {
                for (let i = 0; i < promptData.classic.pre.length; i++) {
                    const __prompt = promptData.classic.pre[i];
                    newPromptInput[__prompt] = '';
                }
            }
        }
        else {
            if (promptData && promptData.lab) {
                if (this.props.initialStylesV1 === true) {
                    for (let i = 0; i < promptData.lab.leadership_styles_initial.length; i++) {
                        const __prompt = promptData.lab.leadership_styles_initial[i];
                        newPromptInput[__prompt] = '';
                    }
                }
                else {
                    for (let i = 0; i < promptData.lab.pre.length; i++) {
                        const __prompt = promptData.lab.pre[i];
                        newPromptInput[__prompt] = '';
                    }
                }

            }
        }
        this.setState({
            promptData,
            promptInput: newPromptInput
        });

        executeQueryPostAuth("data/results/" + this.props.gameID, {}, this.props.user, (postResponse) => {
            executeQueryGetAuth("data/results/" + this.props.gameID, {}, this.props.user, (objectiveResponse) => {
                const response = JSON.parse(objectiveResponse.text);

                const {
                    blockObjectives
                } = this.props;

                // Mark objectives viewed if we're blocking them
                const hasViewedAllObjectives = blockObjectives === true;

                this.setState({
                    loadingObjectiveData: false,
                    objectiveData: response,
                    hasViewedAllObjectives
                });
            });
        });
    }

    executeAI = (journal: string) => {
        this.setState({
            aiCoachLoading: true
        });

        let gameID = this.props.gameID;

        executeQueryGetAuth("ai/coaching/" + gameID + "/pre?possibleJournal=" + journal, {

        }, this.props.user, (data) => {
            let incomingData = data.body;
            this.setState({
                aiCoachLoading: false,
                hasEvaluated: true,
                aiCoachResults: incomingData
            })
        });
    }
    executeFinalPass = (journal: string) => {
        executeQueryGetAuth("ai/coaching/1?possibleJournal=" + journal, {

        }, this.props.user, (data) => {
            let incomingData = data.body;
            this.setState({
                finalPassResults: incomingData
            })
        });
    }

    renderGraphic = () => {
        if (this.state.showPreGraph === true) {
            return <div>
                <SingleOverview defaultSelectedTab={"overview_nav_2"} hideDownloadPrompt={true} forceNoEdit={false} targetUserID={this.props.user.id.toString()} chartMode={true} chartScenario='scenario_1' user={this.props.user} />
            </div>
        }

        const {
            blockObjectives
        } = this.props;

        const hideObjectiveView = blockObjectives === true;

        const {
            viewGraphic,
            objectiveData
        } = this.state;

        if (viewGraphic) {
            return <span style={{ flexGrow: 1, flexBasis: 0 }}>
                <LoadableSVG imgURL={this.props.jouranlGraphic} />
            </span>
        }

        if (hideObjectiveView) {
            return <span style={{ flexGrow: 1, flexBasis: 0 }}>
                <p style={{ textAlign: 'center' }}>
                    <H3>This play has no objectives. Plan using your best judgement.</H3>
                </p>
            </span>
        }

        // Render the objectives
        return <span style={{ flexGrow: 1, flexBasis: 0 }}>
            <KryPlayDetails
                hideScore={true}
                customWarningStyle={{ right: '25px', top: '15px' }}
                user={this.props.user}
                selectedGame={objectiveData}
                canEditJournal={false}
                inSimulator={true}
                globalConfigs={{}}
                hasSeenAllCategories={() => {
                    this.setState({
                        hasViewedAllObjectives: true
                    });
                }}
                updateJournalDataInline={(stage: string, payload: string, gameID: string) => {
                    // ignore this, we aren't letting them edit in this mode
                }}
            />
        </span>
    }

    calculate_is_changes_not_ready = () => {
        const {
            promptInput,
            hasEvaluated
        } = this.state;

        if (hasEvaluated === false) return true;

        if (promptInput === undefined) return true;

        const keys = Object.keys(promptInput);

        if (keys.length === 0) return true;

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];

            if (key !== undefined && key !== "undefined") {
                const value = promptInput[key];

                if (value.length <= 0) {
                    return true;
                }
            }
        }

        return false;
    }

    renderJournalWarning = () => {
        const {
            hasViewedObjectives,
            hasViewedAllObjectives
        } = this.state;

        if (this.calculate_is_changes_not_ready() === false) {
            return '';
        }

        if (this.props.initialStylesV1 !== true) {
            if (hasViewedObjectives === false) {
                return 'You should view the objectives before committing to your plan'
            }

            if (hasViewedAllObjectives === false) {
                return 'You have not viewed all of the objective categories yet'
            }
        }

        if (this.state.hasEvaluated === false) {
            return 'You should evaluate your plan before committing to it';
        }

        return 'You might want to write a more detailed plan.';
    }

    calculateShouldShowGraph = () => {
        if (this.props.showPreGraph === true) {
            return true;
        }

        return false;
    }

    calculate_journal_as_string_without_key = () => {
        let lines = [];

        const {
            promptInput
        } = this.state;

        const keys = promptInput ? Object.keys(promptInput) : [];

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];

            if (key !== undefined && key !== "undefined") {
                const value = promptInput[key];
                lines.push(`${value}\n`);
            }
        }

        return lines.join('\n');
    }

    calculate_journal_as_string = () => {
        let lines = [];

        const {
            promptInput
        } = this.state;

        const keys = promptInput ? Object.keys(promptInput) : [];

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];

            if (key !== undefined && key !== "undefined") {
                const value = promptInput[key];
                lines.push(`${value}`);
            }
        }

        return lines.join('\n');
    }

    renderJournal = () => {

        let prompts = [];

        if (this.props.user.classicMode === 2) {
            if (this.state.promptData && this.state.promptData.classic) {
                for (let i = 0; i < this.state.promptData.classic.pre.length; i++) {
                    const __prompt = this.state.promptData.classic.pre[i];
                    const value = this.state.promptInput[__prompt];

                    prompts.push(
                        <div>
                            <H4 key={i}>{__prompt}</H4>
                            <TextAreaV2 value={value} onChange={(event) => {
                                this.setState({
                                    promptInput: Object.assign(this.state.promptInput, { [__prompt]: event.target.value })
                                });
                            }} className={"journalTextArea"} rows={5} style={{ width: '100%', height: '90px' }}></TextAreaV2>
                        </div>
                    );
                }
            }
        }
        else {
            if (this.state.promptData && this.state.promptData.lab) {
                if (this.props.initialStylesV1 === true) {
                    for (let i = 0; i < this.state.promptData.lab.leadership_styles_initial.length; i++) {
                        const __prompt = this.state.promptData.lab.leadership_styles_initial[i];
                        const value = this.state.promptInput[__prompt];

                        prompts.push(
                            <div>
                                <H4 key={i}>{__prompt}</H4>
                                <TextAreaV2 value={value} onChange={(event) => {
                                    this.setState({
                                        promptInput: Object.assign(this.state.promptInput, { [__prompt]: event.target.value })
                                    });
                                }} className={"journalTextArea"} rows={5} style={{ width: '100%', height: '90px', minWidth: '550px' }}></TextAreaV2>
                            </div>
                        );
                    }
                }
                else {
                    for (let i = 0; i < this.state.promptData.lab.pre.length; i++) {
                        const __prompt = this.state.promptData.lab.pre[i];
                        const value = this.state.promptInput[__prompt];

                        prompts.push(
                            <div>
                                <H4 key={i}>{__prompt}</H4>
                                <TextAreaV2 value={value} onChange={(event) => {
                                    this.setState({
                                        promptInput: Object.assign(this.state.promptInput, { [__prompt]: event.target.value })
                                    });
                                }} className={"journalTextArea"} rows={5} style={{ width: '100%', height: '90px', minWidth: '550px' }}></TextAreaV2>
                            </div>
                        );
                    }

                }
            }
        }

        return <span style={{ flexShrink: 1, transition: 'all 2s', display: 'flex', flexWrap: 'wrap', flexDirection: 'column', gap: '10px' }}>
            {prompts}

            {/* {this.props.initialStylesV1 === true ? undefined : <Button style={{ width: '200px', height: '45px' }} intent={Intent.PRIMARY} disabled={this.state.aiCoachLoading} onClick={() => {
                this.executeAI(this.calculate_journal_as_string_without_key());
            }}>Analyze My Plan</Button>} */}

            {this.state.aiCoachResults ? <CoachFeedback content={<span>
                <h3>{this.state.aiCoachResults.question}</h3>
                <p>
                    <h4>Strengths</h4>
                    {this.state.aiCoachResults.strengths.map((prompt, index) => (
                        <React.Fragment key={index}>
                            <span style={{ fontSize: '1.1rem' }}>{"- " + prompt}</span>
                            <br />
                        </React.Fragment>
                    ))}
                    <hr />
                    <h4>Weaknesses</h4>
                    {this.state.aiCoachResults.weaknesses.map((prompt, index) => (
                        <React.Fragment key={index}>
                            <span style={{ fontSize: '1.1rem' }}>{"- " + prompt}</span>
                            <br />
                        </React.Fragment>
                    ))}
                </p>
            </span>} /> : <span />}

            {this.state.aiCoachLoading ? <Spinner /> : <span />}
        </span>
    }

    aiButtonTooltip = () => {
        if (this.shouldEnableAIButton() === true) {
            return "You need to view all of the objectives before you can analyze your plan.";
        }

        return "";
    }

    calculateDoesPromptInputHaveEnough = () => {
        try {
            const {
                promptInput
            } = this.state;

            if (promptInput === undefined) return false;

            const keys = Object.keys(promptInput);

            if (keys.length === 0) return false;

            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];

                if (key !== undefined && key !== "undefined") {
                    const value = promptInput[key];

                    if (value.length <= 0) {
                        return false;
                    }
                }
            }

            return true;
        }
        catch (e) {
            return false;
        }
    }

    shouldEnableAIButton = () => {
        const {
            hasEvaluated,
            hasViewedAllObjectives
        } = this.state;

        if (this.props.initialStylesV1 !== true) {
            if (hasViewedAllObjectives === false) {
                return true;
            }
        }

        if (this.calculateDoesPromptInputHaveEnough() == false) {
            return true;
        }

        if (hasEvaluated === true) {
            return true;
        }

        return false;
    }

    renderChevron = (active: boolean) => {
        if (active === false) {
            return <span style={{
                float: 'left',
                display: 'block',
                height: '10px',
                width: '48px'
            }}>
            </span>
        }

        return <span>
            <Icon iconSize={32} style={{ marginRight: '8px', marginLeft: '8px', marginTop: '10px', float: 'left' }} icon='chevron-right' />
        </span>
    }

    renderGraphicButton = () => {
        return <span style={{ display: 'block' }}>
            {this.renderChevron(this.state.viewGraphic)}
            <Button onClick={() => { this.setState({ viewGraphic: true }) }} large style={{ display: 'block', width: '350px', height: '50px', marginTop: '30px', marginLeft: '20px' }} intent={Intent.PRIMARY}>Review the Lesson</Button>
        </span>
    }

    renderObjectivesButton = () => {
        return <span style={{ display: 'block' }}>
            {this.renderChevron(this.state.viewGraphic === false && this.state.showPreGraph === false)}
            <Button onClick={() => { this.setState({ viewGraphic: false, hasViewedObjectives: true, showPreGraph: false }) }} large style={{ display: 'block', width: '350px', marginTop: '10px', height: '50px', marginLeft: '20px' }} intent={Intent.PRIMARY}>Plan using Objectives</Button>
        </span>
    }

    renderGraphButton = () => {
        if (this.calculateShouldShowGraph() === false) {
            return <span />
        }

        return <span style={{ display: 'block' }}>
            {this.renderChevron(this.state.showPreGraph === true)}
            <Button onClick={() => {
                this.setState({
                    showPreGraph: true,
                    viewGraphic: false,

                });

            }} large style={{ display: 'block', width: '350px', marginTop: '10px', height: '50px', marginLeft: '20px' }} intent={Intent.NONE}>Compare with Initial Play</Button>
        </span>
    }

    renderGoHomeButton = () => {
        return <span style={{ display: 'block' }}>
            {this.renderChevron(false)}
            <Button onClick={() => {
                this.props.goBackHome();
            }} large style={{ display: 'block', width: '350px', marginTop: '10px', height: '50px', marginLeft: '20px' }} intent={Intent.NONE}>Go Back</Button>
        </span>
    }

    renderAIButton = () => {
        return <span style={{ display: 'block' }}>
            {this.renderChevron(false)}
            <Tooltip content={this.aiButtonTooltip()}>
                <Button disabled={this.shouldEnableAIButton()} onClick={() => { this.executeAI(this.calculate_journal_as_string_without_key()); }} large style={{ display: 'block', width: '350px', height: '50px', marginLeft: '49px' }} intent={Intent.PRIMARY}>Analyze My Plan</Button>
            </Tooltip>
        </span>
    }

    renderSaveButton = () => {
        const {
            hasViewedObjectives,
            hasViewedAllObjectives
        } = this.state;

        const {
            cachedDefaultJournal
        } = this.props;

        let warningMessage: any = this.renderJournalWarning();
        let shouldBeEnabled = this.calculate_is_changes_not_ready() === false;

        // Force the viewer to look at the objectives first
        if (this.props.initialStylesV1 !== true) {
            if (hasViewedObjectives === false) {
                shouldBeEnabled = false;
            }

            // Force the viewer to look at all the objective categories as well
            if (hasViewedAllObjectives === false) {
                shouldBeEnabled = false;
            }
        }

        // If the cached journal was given, then 
        // allow the button to immediately be pressed
        if (cachedDefaultJournal !== undefined) {
            shouldBeEnabled = true;
            warningMessage = undefined;
        }

        const saveButton = <Button disabled={!shouldBeEnabled} large style={{ display: 'block', width: '350px', marginTop: '10px', height: '60px', marginLeft: '50px' }} onClick={() => {
            this.props.recieveJournal(this.calculate_journal_as_string());
            // this.setState({
            //     stagePromptForFinalPass: this.calculate_journal_as_string()
            // });

            // this.executeFinalPass(this.calculate_journal_as_string());
        }} intent={Intent.SUCCESS}>Save & Execute your Plan</Button>

        if (warningMessage === undefined) {
            return saveButton;
        }

        return <Tooltip content={warningMessage}>
            {saveButton}
        </Tooltip>
    }

    renderAudioComponent = () => {
        return <CoachingAudio
            audioSource={this.props.coachingAudio}
            donePlaying={() => {
                this.setState({
                    hasAudioFinishedOnce: true
                });
            }}
        />
    }

    renderDisplayRegion = () => {
        if (this.props.initialStylesV1 === true) {
            return <div style={{ height: '80px' }}>

            </div>
        }

        return this.renderGraphic();
    }

    renderControlRegion = () => {
        return <span style={{ flexGrow: 0 }}>
            <span style={{ display: 'flex', flexDirection: 'row' }}>
                <span style={{ flexGrow: 1 }}>
                    {this.renderJournal()}
                </span>
                <span style={{ width: '400px' }}>
                    {this.renderGraphicButton()}
                    {this.props.initialStylesV1 === true ? undefined : this.renderObjectivesButton()}
                    {this.renderGraphButton()}
                    {this.props.initialStylesV1 === true ? undefined : this.renderAIButton()}
                    {this.renderGoHomeButton()}
                    {this.renderSaveButton()}
                </span>
            </span>
        </span>
    }

    renderStagedPrompt = () => {
        return <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '100px' }}>

            <h3>Before you move on</h3>

            {this.state.finalPassResults === undefined ? <Spinner /> : <Card>
                <h4>{this.state.finalPassResults.encouragement}</h4>
                <p style={{ marginBottom: '20px' }}>
                    {this.state.finalPassResults.prompts.map((prompt, index) => (
                        <React.Fragment key={index}>
                            {prompt}
                            <br />
                        </React.Fragment>
                    ))}
                </p>
                {this.renderJournal()}
            </Card>
            }

            <Button large style={{ display: 'block', width: '350px', marginTop: '10px', height: '60px', marginLeft: 'auto', marginRight: 'auto' }} onClick={() => {
                this.props.recieveJournal(this.calculate_journal_as_string());
            }} intent={Intent.SUCCESS}>Save & Play</Button>
        </div>
    }

    render() {
        const {
            loadingObjectiveData
        } = this.state;

        if (loadingObjectiveData) return <span style={{ display: 'block', width: '100%', margin: 'auto', marginTop: '40px' }}>
            <Spinner />
        </span>

        return <div className="journalParent" style={{ width: '100%' }}>
            {this.renderAudioComponent()}
            <span style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {this.state.stagePromptForFinalPass === "" ?
                    <div>
                        {this.renderDisplayRegion()}
                        {this.renderControlRegion()}
                    </div> : this.renderStagedPrompt()}
            </span>
        </div>
    }
}
